// src/components/QRCodeDialog.tsx
"use client";

import { useState } from "react";
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Check, Download, Share2 } from "lucide-react";
import { useToast } from "@/components/hooks/use-toast";

interface QRCodeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  assistantId: string;
  assistantName: string;
}

export function QRCodeDialog({ 
  isOpen, 
  onOpenChange, 
  assistantId, 
  assistantName 
}: QRCodeDialogProps) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  // Generate the share URL
  const shareUrl = `${window.location.origin}/assistant/${assistantId}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Share URL copied to clipboard"
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy URL:", error);
      toast({
        title: "Copy failed",
        description: "Failed to copy URL to clipboard",
        variant: "destructive"
      });
    }
  };

  const handleDownload = () => {
    try {
      // Get the QR code SVG element
      const svg = document.querySelector('#qr-code-svg') as SVGElement;
      if (!svg) {
        console.error("QR code SVG not found");
        return;
      }

      // Clone the SVG to avoid modifying the original
      const svgClone = svg.cloneNode(true) as SVGElement;
      
      // Add white background for better visibility
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('width', '100%');
      rect.setAttribute('height', '100%');
      rect.setAttribute('fill', 'white');
      svgClone.insertBefore(rect, svgClone.firstChild);

      // Get SVG dimensions
      const svgWidth = parseInt(svg.getAttribute('width') || '200');
      const svgHeight = parseInt(svg.getAttribute('height') || '200');

      // Create canvas with higher resolution for better quality
      const scale = 3;
      const canvas = document.createElement('canvas');
      canvas.width = svgWidth * scale;
      canvas.height = svgHeight * scale;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error("Canvas context not available");
        return;
      }

      // Draw white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Convert SVG to data URL (CSP-safe approach)
      const svgData = new XMLSerializer().serializeToString(svgClone);
      const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);

      const img = new Image();
      
      // Set up the image load handler
      img.onload = () => {
        // Scale and draw the image
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        
        // Convert canvas to blob and trigger download
        canvas.toBlob((blob) => {
          if (!blob) {
            console.error("Failed to create blob");
            toast({
              title: "Download failed",
              description: "Failed to create image file",
              variant: "destructive"
            });
            return;
          }
          
          // Create download link
          const downloadUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `${assistantName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_qr_code.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up
          setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
          
          toast({
            title: "Downloaded!",
            description: "QR code saved successfully"
          });
        }, 'image/png', 1.0);
      };
      
      img.onerror = () => {
        console.error("Failed to load SVG image");
        toast({
          title: "Download failed",
          description: "Failed to process QR code image",
          variant: "destructive"
        });
      };
      
      // Load the image with data URL (CSP-safe)
      img.src = svgDataUrl;
      
    } catch (error) {
      console.error("Failed to download QR code:", error);
      toast({
        title: "Download failed",
        description: "Failed to download QR code",
        variant: "destructive"
      });
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: assistantName,
          text: `Check out this AI assistant: ${assistantName}`,
          url: shareUrl,
        });
      } catch (error) {
        console.error("Failed to share:", error);
      }
    } else {
      // Fallback to copy URL
      handleCopy();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">Share QR Code</DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col items-center space-y-6 py-4">
          {/* Assistant Name */}
          <div className="text-center">
            <h3 className="font-semibold text-lg">{assistantName}</h3>
            <p className="text-sm text-muted-foreground">Scan to access this assistant</p>
          </div>

          {/* QR Code */}
          <div className="bg-white p-6 rounded-lg shadow-lg border-2 border-muted">
            <QRCodeSVG 
              id="qr-code-svg"
              value={shareUrl} 
              size={200}
              level="M"
              includeMargin={true}
            />
          </div>

          {/* Share URL */}
          <div className="w-full space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Share URL:</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1 px-3 py-2 text-sm border rounded-md bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleCopy}
                className="shrink-0"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-3 w-full">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="flex-1 min-w-[120px]"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              className="flex-1 min-w-[120px]"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          <div className="text-xs text-muted-foreground text-center">
            Point your phone's camera at the QR code to open the assistant
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}